/**
 * API client for the NestJS backend. Authentication is cookie-based: the
 * backend sets an HttpOnly `sd_session` cookie and fetch sends credentials.
 * Bearer support remains in memory only for temporary migration clients.
 */

type RequestOptions = Omit<RequestInit, 'body'> & {
  params?: Record<string, string | number | boolean | undefined>;
};
type CsrfTokenResponse = { token: string };

const BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
let authToken: string | null = null;
let unauthorizedHandler: (() => void) | null = null;
let csrfToken: string | null = null;
let csrfTokenGeneration = 0;
let csrfTokenPromise: { generation: number; promise: Promise<string> } | null = null;

export function setAuthToken(token: string | null) { authToken = token; }
export function getAuthToken(): string | null { return authToken; }
export function setUnauthorizedHandler(handler: (() => void) | null) { unauthorizedHandler = handler; }
export function setCsrfToken(token: string | null) {
  csrfToken = token;
  if (token === null) csrfTokenGeneration += 1;
}

export function invalidateCsrfToken(expectedToken?: string): void {
  // Concurrent stale-token retries should share the same next generation.
  if (expectedToken && csrfToken && csrfToken !== expectedToken) return;
  if (!csrfToken && csrfTokenPromise && csrfTokenPromise.generation === csrfTokenGeneration) return;
  if (!csrfToken && !csrfTokenPromise) return;
  csrfToken = null;
  csrfTokenGeneration += 1;
}

function buildUrl(path: string, params?: Record<string, string | number | boolean | undefined>): string {
  const base = path.startsWith('http') ? path : `${BASE_URL}${path.startsWith('/') ? '' : '/'}${path}`;
  const url = new URL(base, window.location.origin);
  for (const [key, value] of Object.entries(params || {})) if (value !== undefined) url.searchParams.set(key, String(value));
  return url.toString();
}

export class ApiError extends Error {
  status: number;
  data: unknown;
  constructor(message: string, status: number, data?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

/**
 * Client-generated (non-server) error messages mapped to translation keys so
 * call-sites with access to `t` can localise them:
 * `translateClientError(err, t)` returns the translated text, or the original
 * message when it is not a known client-side message.
 */
const CLIENT_ERROR_KEYS = {
  'Unable to establish CSRF protection': 'tp.api.csrfUnavailable',
  // Thrown by lib/auth.tsx when a challenge step is submitted without a pending challenge.
  'No MFA challenge active': 'tp.api.noMfaChallenge',
  'No MFA setup challenge active': 'tp.api.noMfaSetup',
  'No new password challenge active': 'tp.api.noNewPasswordChallenge',
} as const;

export function translateClientError(
  error: unknown,
  t: (key: (typeof CLIENT_ERROR_KEYS)[keyof typeof CLIENT_ERROR_KEYS]) => string,
): string {
  const message = error instanceof Error ? error.message : String(error);
  const key = (CLIENT_ERROR_KEYS as Record<string, (typeof CLIENT_ERROR_KEYS)[keyof typeof CLIENT_ERROR_KEYS]>)[message];
  return key ? t(key) : message;
}

function notifyUnauthorized(status: number) { if (status === 401) unauthorizedHandler?.(); }
function requiresCsrfToken(method: string): boolean { return ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method.toUpperCase()); }
function isCsrfTokenResponse(value: unknown): value is CsrfTokenResponse {
  return typeof value === 'object' && value !== null && 'token' in value && typeof (value as CsrfTokenResponse).token === 'string';
}

async function getCsrfToken(): Promise<string> {
  if (csrfToken) return csrfToken;
  const generation = csrfTokenGeneration;
  if (csrfTokenPromise?.generation === generation) return csrfTokenPromise.promise;

  const promise = (async () => {
    const response = await fetch(buildUrl('/auth/csrf'), { credentials: 'include', method: 'GET' });
    const body: unknown = await response.json().catch(() => null);
    if (!response.ok || !isCsrfTokenResponse(body)) throw new ApiError('Unable to establish CSRF protection', response.status, body);
    if (generation !== csrfTokenGeneration) return getCsrfToken();
    csrfToken = body.token;
    return body.token;
  })();
  csrfTokenPromise = { generation, promise };
  try { return await promise; } finally {
    if (csrfTokenPromise?.promise === promise) csrfTokenPromise = null;
  }
}

async function addCsrfHeader(method: string, headers: Record<string, string>): Promise<void> {
  if (requiresCsrfToken(method)) headers['X-CSRF-Token'] = await getCsrfToken();
}

let refreshPromise: Promise<boolean> | null = null;
async function attemptRefresh(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      const headers = { 'Content-Type': 'application/json' };
      await addCsrfHeader('POST', headers);
      const res = await fetch(buildUrl('/auth/refresh'), { method: 'POST', credentials: 'include', headers });
      return res.ok;
    } catch { return false; } finally { refreshPromise = null; }
  })();
  return refreshPromise;
}

async function parseError(res: Response): Promise<never> {
  const errorBody = await res.json().catch(() => ({ message: res.statusText }));
  notifyUnauthorized(res.status);
  throw new ApiError(errorBody.message || errorBody.error || res.statusText, res.status, errorBody);
}

function isCsrfError(status: number, body: unknown): boolean {
  return status === 403 && typeof body === 'object' && body !== null && (body as { message?: string }).message === 'Invalid CSRF token';
}

async function request<T>(method: string, path: string, body?: unknown, options?: RequestOptions): Promise<T> {
  const { params, ...fetchOpts } = options || {};
  const headers: Record<string, string> = { ...((fetchOpts.headers as Record<string, string>) || {}), ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}) };
  const token = getAuthToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  await addCsrfHeader(method, headers);
  const res = await fetch(buildUrl(path, params), { ...fetchOpts, method, credentials: 'include', headers, body: body !== undefined ? JSON.stringify(body) : undefined });
  // Retry once with a fresh CSRF token on token mismatch (stale in-memory token)
  if (res.status === 403 && requiresCsrfToken(method)) {
    const errorBody = await res.json().catch(() => null);
    if (isCsrfError(res.status, errorBody)) {
      invalidateCsrfToken(headers['X-CSRF-Token']);
      const freshHeaders = { ...headers };
      await addCsrfHeader(method, freshHeaders);
      const retryRes = await fetch(buildUrl(path, params), { ...fetchOpts, method, credentials: 'include', headers: freshHeaders, body: body !== undefined ? JSON.stringify(body) : undefined });
      if (!retryRes.ok) await parseError(retryRes);
      if (retryRes.status === 204) return undefined as T;
      return retryRes.json();
    }
    notifyUnauthorized(res.status);
    throw new ApiError((errorBody as { message?: string })?.message || res.statusText, res.status, errorBody);
  }
  if (res.status === 401 && !path.includes('/auth/')) {
    const refreshed = await attemptRefresh();
    if (refreshed) {
      // After refresh, also get a fresh CSRF token since cookies may have changed
      invalidateCsrfToken(headers['X-CSRF-Token']);
      const freshHeaders = { ...headers };
      await addCsrfHeader(method, freshHeaders);
      const retryRes = await fetch(buildUrl(path, params), { ...fetchOpts, method, credentials: 'include', headers: freshHeaders, body: body !== undefined ? JSON.stringify(body) : undefined });
      if (!retryRes.ok) await parseError(retryRes);
      if (retryRes.status === 204) return undefined as T;
      return retryRes.json();
    }
  }
  if (!res.ok) await parseError(res);
  if (res.status === 204) return undefined as T;
  return res.json();
}

async function upload<T>(path: string, formData: FormData, options?: RequestOptions): Promise<T> {
  const { params, ...fetchOpts } = options || {};
  const headers: Record<string, string> = { ...((fetchOpts.headers as Record<string, string>) || {}) };
  const token = getAuthToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  await addCsrfHeader('POST', headers);
  const res = await fetch(buildUrl(path, params), { ...fetchOpts, method: 'POST', credentials: 'include', headers, body: formData });
  if (!res.ok) await parseError(res);
  if (res.status === 204) return undefined as T;
  return res.json();
}

async function download(path: string, options?: RequestOptions): Promise<Blob> {
  const { params, ...fetchOpts } = options || {};
  const headers: Record<string, string> = { ...((fetchOpts.headers as Record<string, string>) || {}) };
  const token = getAuthToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(buildUrl(path, params), { ...fetchOpts, method: 'GET', credentials: 'include', headers });
  if (!res.ok) await parseError(res);
  return res.blob();
}

/** POST a JSON body and receive a binary response (CSV/Excel/PDF/ZIP export downloads). */
async function downloadPost(path: string, body: unknown, options?: RequestOptions): Promise<Blob> {
  const { params, ...fetchOpts } = options || {};
  const headers: Record<string, string> = { ...((fetchOpts.headers as Record<string, string>) || {}), 'Content-Type': 'application/json' };
  const token = getAuthToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  await addCsrfHeader('POST', headers);
  const res = await fetch(buildUrl(path, params), { ...fetchOpts, method: 'POST', credentials: 'include', headers, body: JSON.stringify(body) });
  if (res.status === 403) {
    const errorBody = await res.clone().json().catch(() => null);
    if (isCsrfError(res.status, errorBody)) {
      invalidateCsrfToken(headers['X-CSRF-Token']);
      const freshHeaders = { ...headers };
      await addCsrfHeader('POST', freshHeaders);
      const retryRes = await fetch(buildUrl(path, params), { ...fetchOpts, method: 'POST', credentials: 'include', headers: freshHeaders, body: JSON.stringify(body) });
      if (!retryRes.ok) await parseError(retryRes);
      return retryRes.blob();
    }
  }
  if (!res.ok) await parseError(res);
  return res.blob();
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>('GET', path, undefined, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) => request<T>('POST', path, body, options),
  upload: <T>(path: string, formData: FormData, options?: RequestOptions) => upload<T>(path, formData, options),
  download: (path: string, options?: RequestOptions) => download(path, options),
  downloadPost: (path: string, body: unknown, options?: RequestOptions) => downloadPost(path, body, options),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) => request<T>('PUT', path, body, options),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) => request<T>('PATCH', path, body, options),
  delete: <T>(path: string, options?: RequestOptions) => request<T>('DELETE', path, undefined, options),
  deleteWithBody: <T>(path: string, body: unknown, options?: RequestOptions) => request<T>('DELETE', path, body, options),
};
